/**
 * @file changes.ts
 * @description Тексты полосы черновика и окна публикации «вида сайта»
 * (phase19.16 Р1; uiux-design-r1 §0.1, §6; SB60, SB79): «Не опубликовано:
 * оформление 3, меню 1», последняя правка, объяснение «чьи правки ждут».
 *
 * Чистые функции без React — их проверяют тесты и используют полоса,
 * шторка «Что изменено» и окно публикации, поэтому формулировки не
 * расходятся между местами.
 */

import {
  SITE_VIEW_PART_LABELS,
  SITE_VIEW_PART_RIGHTS,
  SITE_VIEW_PERMISSION_LABELS,
  type SiteViewPart,
} from "@chaiberi/shared";

import { formatShopDate, formatShopDateTime } from "../shop-time";
import type { SiteBlockedPart, SiteChangedPart, SiteViewChanges, SiteViewUserRef } from "./types";

/** «оформление 3» — название части и число строк изменений. */
export function partCountLabel(part: Pick<SiteChangedPart, "part" | "count">): string {
  return `${SITE_VIEW_PART_LABELS[part.part]} ${part.count}`;
}

/** «оформление 3, меню 1»; пусто — пустая строка. */
export function draftSummary(changes: Pick<SiteViewChanges, "changedParts">): string {
  return changes.changedParts.map(partCountLabel).join(", ");
}

/** Самая поздняя правка среди изменённых частей (для «последняя правка — …»). */
export function lastEdit(
  changes: Pick<SiteViewChanges, "changedParts">,
): { by: SiteViewUserRef; at: string } | null {
  let best: { by: SiteViewUserRef; at: string } | null = null;
  for (const part of changes.changedParts) {
    if (!part.editedBy || !part.editedAt) continue;
    if (!best || part.editedAt > best.at) best = { by: part.editedBy, at: part.editedAt };
  }
  return best;
}

/**
 * «сегодня 14:32» для правки этого дня (по поясу магазина), иначе
 * «05.10.2026, 18:02». `now` — параметром, чтобы тесты не зависели от часов.
 */
export function formatEditMoment(iso: string, now: Date = new Date()): string {
  const full = formatShopDateTime(iso);
  if (formatShopDate(iso) !== formatShopDate(now.toISOString())) return full;
  const time = full.split(", ")[1];
  return time ? `сегодня ${time}` : full;
}

/** Имя автора правки: «ваши правки», если правил сам сотрудник. */
export function editorName(by: SiteViewUserRef | null, currentUserId: string | null): string {
  if (!by) return "платформа";
  return by.id !== null && by.id === currentUserId ? "ваши правки" : by.name;
}

/**
 * Полоса для того, кто опубликовать не может (SB60, uiux §6 «Не хватает
 * прав»): «меню 1 (ваши правки), оформление 3 (Анна Кравец)».
 */
export function partsWithEditors(
  changes: Pick<SiteViewChanges, "changedParts">,
  currentUserId: string | null,
): string {
  return changes.changedParts
    .map((part) => `${partCountLabel(part)} (${editorName(part.editedBy, currentUserId)})`)
    .join(", ");
}

/** Права, без которых публикация недоступна, — по порядку частей, без повторов. */
function blockingPermissions(blocked: readonly SiteBlockedPart[]): string[] {
  const result: string[] = [];
  for (const item of blocked) {
    const label = SITE_VIEW_PERMISSION_LABELS[item.requiredPermission];
    if (!result.includes(label)) result.push(label);
  }
  return result;
}

/**
 * Объяснение, почему «Опубликовать» неактивна (SB60, SB79 — только текст):
 * «Опубликовать может владелец магазина или сотрудник с правом настроек
 * магазина: в черновике есть правки оформления. Ваши правки меню уйдут на
 * сайт вместе с ними.»
 */
export function blockedExplanation(
  changes: Pick<SiteViewChanges, "changedParts" | "blockedBy">,
  currentUserId: string | null,
): string {
  if (changes.blockedBy.length === 0) return "";
  const rights = blockingPermissions(changes.blockedBy).join(" и ");
  const blockedLabels = changes.blockedBy.map((b) => SITE_VIEW_PART_LABELS[b.part]).join(", ");
  let text = `Опубликовать может владелец магазина или сотрудник с ${rights}: в черновике есть правки (${blockedLabels}).`;
  const blockedSet = new Set(changes.blockedBy.map((b) => b.part));
  const mine = changes.changedParts.filter(
    (p) => !blockedSet.has(p.part) && p.editedBy?.id != null && p.editedBy.id === currentUserId,
  );
  if (mine.length > 0) {
    const labels = mine.map((p) => SITE_VIEW_PART_LABELS[p.part]).join(", ");
    text += ` Ваши правки (${labels}) уйдут на сайт вместе с ними.`;
  }
  return text;
}

/** Части, которые сотрудник может отменить (право правки части). */
export function discardableParts(
  changes: Pick<SiteViewChanges, "changedParts">,
): SiteViewPart[] {
  return changes.changedParts.filter((p) => p.canDiscard).map((p) => p.part);
}

/** Право, нужное для правки части, — для текста «нет прав». */
export function editPermissionLabel(part: SiteViewPart): string {
  return SITE_VIEW_PERMISSION_LABELS[SITE_VIEW_PART_RIGHTS[part].edit];
}

/** Строка «до → после» для списка изменений. */
export function changeLine(item: { label: string; before: string | null; after: string | null }): string {
  if (item.before === null && item.after === null) return item.label;
  return `${item.label}: ${item.before ?? "—"} → ${item.after ?? "—"}`;
}

/**
 * Название части для заголовка карточки: «Оформление · 3». Названия частей в
 * shared — со строчной (для середины фразы: «Изменения отменены: меню»).
 */
export function partHeading(label: string): string {
  return label.charAt(0).toLocaleUpperCase("ru") + label.slice(1);
}
